import GiftCardProduct from './GiftCardProduct';
import PDPVideo from './PDPVideo';

let theme = window.theme || {};

export default () => {
  const quickView = document.querySelector('[data-quick-view-content]');

  const observer = new MutationObserver(() => {
    try {
      // It will prevent an block on the cart page. Yotpo is not functional on the cart page
      if (window.location.pathname !== '/cart') {
        window.yotpo.refreshWidgets();
      }
    } catch (error) {
      console.log('err:', error);
      console.log('Quickview: Error in yotpo refresh');
    }

    document.dispatchEvent(new Event('shouldProcessProductData'));
    document.dispatchEvent(new Event('quickViewDidLoad'));

    observer.disconnect();
  });

  // Listen for the QuickView load event to initialize PDPVideo
  document.addEventListener('quickViewDidLoad', () => {
    try {
      // Get the newly created QuickView container
      const quickViewContainer = document.querySelector('[data-quick-view-content]');
      if (!quickViewContainer) {
        console.error('QuickView: Cannot find QuickView content');
        return;
      }

      // Fix the Yotpo error with defensive check
      if (window.location.pathname !== '/cart') {
        if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
          window.yotpo.refreshWidgets();
        }
      }

      // IMPORTANT: First check if video data exists for this product
      const videoDataElement = quickViewContainer.querySelector('[data-product-video-data]');
      if (!videoDataElement) {
        return;
      }

      // Create a new PDPVideo instance
      const pdpVideo = new PDPVideo(quickViewContainer);

      // Initialize it with the current context
      pdpVideo.init(quickViewContainer);

      // Force video initialization
      try {
        // Directly get video data from the data attribute
        let videoData;
        try {
          videoData = JSON.parse(videoDataElement.textContent);
        } catch (e) {
          console.error('QuickView: Error parsing video data', e);
          return;
        }

        if (!videoData || !videoData.videos || videoData.videos.length === 0) {
          return;
        }

        // Get current barcode first
        const currentBarcode = getCurrentBarcode(quickViewContainer, videoData);
        if (!currentBarcode) return;

        // Filter videos for current variant
        const variantVideos = videoData.videos.filter((video) => video.barcode === currentBarcode);
        if (!variantVideos.length) return;

        // Initialize videos through PDPVideo instance
        if (typeof pdpVideo.checkAndAddVideosToDom === 'function') {
          pdpVideo.checkAndAddVideosToDom(variantVideos, quickViewContainer);
        }

        // Wait for swiper to be initialized
        if (typeof pdpVideo.checkSwipersInitialized === 'function') {
          pdpVideo.checkSwipersInitialized().then(() => {
            pdpVideo.updateCarousels();
          });
        }
      } catch (error) {
        console.error('QuickView: Error during forced video initialization', error);
      }
    } catch (err) {
      console.error('QuickView: Error initializing components', err);
    }
  });

  // Force load all videos for all variants when QuickView is opened
  document.addEventListener('quickViewLoaded', () => {
    const quickViewContainer = document.querySelector('.QuickView__modal.Modal--open');
    if (!quickViewContainer) return;

    // Initialize PDPVideo instance
    if (!quickViewContainer.pdpVideoInstance) {
      quickViewContainer.pdpVideoInstance = new PDPVideo(quickViewContainer);
    }

    // Listen for video initialization
    quickViewContainer.addEventListener(PDPVideo.VIDEO_EVENTS.INIT_READY, () => {
      const videoData = quickViewContainer.pdpVideoInstance?.videoData;
      if (!videoData?.videos?.length) return;

      // Get the currently selected variant barcode through multiple fallback methods
      let currentBarcode = getCurrentBarcode(quickViewContainer, videoData);
      if (!currentBarcode) {
        const variantIdElement = quickViewContainer.querySelector('[data-variant-id]');
        if (variantIdElement) {
          const variantId = variantIdElement.getAttribute('data-variant-id');

          // Try to find the variant in the product data
          if (
            quickViewContainer.pdpVideoInstance.productData &&
            quickViewContainer.pdpVideoInstance.productData.variants
          ) {
            const currentVariant = quickViewContainer.pdpVideoInstance.productData.variants.find(
              (v) => v.id.toString() === variantId,
            );
            if (currentVariant && currentVariant.barcode) {
              currentBarcode = currentVariant.barcode;
            }
          }
        }
      }

      quickViewContainer.pdpVideoInstance.currentVariantBarcode = currentBarcode;

      // Hide all videos first
      const allVideoSlides = quickViewContainer.querySelectorAll('.swiper-slide[data-video-variant-barcode]');
      allVideoSlides.forEach((slide) => {
        slide.style.display = 'none';
      });

      // Process videos for the current variant
      quickViewContainer.pdpVideoInstance.processVideosForVariant(currentBarcode);

      // Add videos to DOM
      const currentVariantVideos = videoData.videos.filter((video) => video.barcode === currentBarcode);
      if (currentVariantVideos.length > 0) {
        quickViewContainer.pdpVideoInstance.checkAndAddVideosToDom(currentVariantVideos, quickViewContainer);
      }

      // Update video visibility and carousels
      quickViewContainer.pdpVideoInstance.updateVideoVisibility(currentBarcode, quickViewContainer);

      // Force slide to first position and update carousels
      const mainSwiperEl = quickViewContainer.querySelector('.swiper-main');
      const thumbsSwiperEl = quickViewContainer.querySelector('.swiper-thumbs');
      if (mainSwiperEl?.swiper && thumbsSwiperEl?.swiper) {
        mainSwiperEl.swiper.slideTo(0, 0, false);
        thumbsSwiperEl.swiper.slideTo(0, 0, false);
        quickViewContainer.pdpVideoInstance.updateCarousels();
      }

      // Signal that QuickView video is ready
      quickViewContainer.dispatchEvent(new CustomEvent(PDPVideo.VIDEO_EVENTS.QUICKVIEW_VIDEO_READY));
    });
  });

  // Handle variant changes in QuickView
  document.addEventListener('variantChange', (e) => {
    const quickViewContainer = document.querySelector('.QuickView[aria-hidden="false"]');
    if (!quickViewContainer || !quickViewContainer.pdpVideoInstance) return;

    const variant = e.detail.variant;
    const variantBarcode = variant.barcode || variant.sku;

    // Force process videos for the new variant
    quickViewContainer.pdpVideoInstance.processVideosForVariant(variantBarcode);

    // Update the ProductOptions data-variant-barcode attribute for future reference
    const productOptions = quickViewContainer.querySelector('.ProductOptions');
    if (productOptions) {
      productOptions.setAttribute('data-variant-barcode', variantBarcode);
    }

    // First update video visibility
    quickViewContainer.pdpVideoInstance.updateVideoVisibility(variantBarcode, quickViewContainer);

    // Then reset slides and update carousels once
    const mainSwiperEl = quickViewContainer.querySelector('.swiper-main');
    const thumbsSwiperEl = quickViewContainer.querySelector('.swiper-thumbs');
    if (mainSwiperEl?.swiper && thumbsSwiperEl?.swiper) {
      // Update swipers first to ensure all slides are registered
      mainSwiperEl.swiper.update();
      thumbsSwiperEl.swiper.update();

      // Then reset to first slide
      mainSwiperEl.swiper.slideTo(0, 0, false);
      thumbsSwiperEl.swiper.slideTo(0, 0, false);

      // Finally update carousels to sync everything
      quickViewContainer.pdpVideoInstance.updateCarousels();
    }
  });

  document.addEventListener('click', (e) => {
    const target = e.target.closest('[data-quick-view-button]');
    if (target) {
      observer.observe(quickView, {
        childList: true,
        subtree: true,
      });

      const handle = target.getAttribute('data-product-handle');

      if (handle) {
        theme.quickView = theme.quickView || {};

        fetch(`/products/${handle}?view=quick-view`)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const quickViewContent = responseHTML.querySelector('[data-quick-view-content]');

            if (quickViewContent) {
              quickView.innerHTML = quickViewContent.innerHTML;
              document.dispatchEvent(new Event('quickViewLoaded'));

              // Initialize gift card product if needed
              if (quickView.querySelector('[data-gift-card-product]')) {
                GiftCardProduct();
              }

              // Initialize PDPVideo for the QuickView
              if (quickView.querySelector('[data-product-video-data]')) {
                // PDPVideo will be initialized by the quickViewLoaded event listener
              }
            }
          })
          .catch((error) => {
            console.error('Error loading quick view:', error);
          });
      }
    }
  });
};

// Helper function to get the current barcode from various sources
function getCurrentBarcode(container, videoData) {
  // Try to get from ProductOptions data attribute first
  const productOptions = container.querySelector('.ProductOptions');
  if (productOptions && productOptions.getAttribute('data-variant-barcode')) {
    const barcode = productOptions.getAttribute('data-variant-barcode');
    return barcode;
  }

  // Try to get from the selected variant buttons
  const selectedColorButton = container.querySelector(
    '[data-custom-option-selector="option1"] [data-custom-variant-selector].ProductOption__variant--selected',
  );
  const selectedSizeButton = container.querySelector(
    '[data-custom-option-selector="option2"] [data-custom-variant-selector].ProductOption__variant--selected',
  );

  // Check if the selected color button has a barcode attribute
  if (selectedColorButton && selectedColorButton.getAttribute('data-variant-barcode')) {
    const barcode = selectedColorButton.getAttribute('data-variant-barcode');
    return barcode;
  }

  // Check if the selected size button has a barcode attribute
  if (selectedSizeButton && selectedSizeButton.getAttribute('data-variant-barcode')) {
    const barcode = selectedSizeButton.getAttribute('data-variant-barcode');
    return barcode;
  }

  // Try to get from style number value
  const styleNumberValue = container.querySelector('[data-style-number-value]');
  if (styleNumberValue && styleNumberValue.textContent) {
    const styleNumber = styleNumberValue.textContent.trim();
    return styleNumber;
  }

  // Try to get from variant ID
  const variantIdElement = container.querySelector('[data-variant-id]');
  if (variantIdElement) {
    const variantId = variantIdElement.getAttribute('data-variant-id');
    // If we have product data, try to find the barcode
    if (window.productData && window.productData.variants) {
      const variant = window.productData.variants.find((v) => v.id.toString() === variantId);
      if (variant && variant.barcode) {
        return variant.barcode;
      }
    }
  }

  // Try to get from the first selected option
  const firstSelectedOption = container.querySelector('.ProductOption__variant--selected');
  if (firstSelectedOption) {
    const color = firstSelectedOption.getAttribute('data-value');

    // Try to find a video that matches this color
    if (videoData.videos && videoData.videos.length > 0) {
      // Look for a video with a barcode that contains this color
      for (const video of videoData.videos) {
        if (video.barcode && video.barcode.includes(color)) {
          return video.barcode;
        }
      }
    }
  }

  // Try to get from the current variant ID
  const currentBarcode = getCurrentBarcode(container, videoData);
  if (!currentBarcode) {
    const variantIdElement = container.querySelector('[data-variant-id]');
    if (variantIdElement) {
      const variantId = variantIdElement.getAttribute('data-variant-id');

      // Try to find the variant in the product data
      if (container.pdpVideoInstance.productData && container.pdpVideoInstance.productData.variants) {
        const currentVariant = container.pdpVideoInstance.productData.variants.find(
          (v) => v.id.toString() === variantId,
        );
        if (currentVariant && currentVariant.barcode) {
          return currentVariant.barcode;
        }
      }
    }
  }

  // If we don't have a barcode, use the first available one
  if (!currentBarcode && videoData.barcodes && videoData.barcodes.length > 0) {
    // IMPORTANT: We need to make sure we're using the correct first barcode
    // Get the first barcode from the actual DOM
    const firstVariantOption = container.querySelector('.ProductOption__variant:first-child');
    if (firstVariantOption) {
      const firstColor = firstVariantOption.getAttribute('data-value');

      // Find the corresponding barcode for this color
      const variantOptions = container.querySelectorAll('[data-variant-json]');
      for (const option of variantOptions) {
        try {
          const variantData = JSON.parse(option.getAttribute('data-variant-json'));
          if (variantData && variantData.option1 === firstColor) {
            return variantData.barcode || variantData.sku;
          }
        } catch (e) {
          console.error('QuickView: Error parsing variant JSON', e);
        }
      }
    }

    // If still no barcode, use the first one from the video data
    return videoData.barcodes[0];
  }

  return null;
}
